/**
 * Nepali Marriage (21-card) — server-authoritative session engine.
 *
 * Rules implemented (see docs/games/MARRIAGE.md; toggles in {@link MarriageConfig}):
 *  - 2..5 players, 3 decks (configurable 3–4) and optional printed jokers. 21 cards each.
 *  - One card is set aside face-down as the TIPLU (joker indicator); the next card starts the discard pile.
 *  - Turn = draw (stock or top discard) → optional SHOW / FINISH → discard.
 *    · Before showing, the top discard may be taken only if it immediately forms a pure sequence or tunnela
 *      with cards in hand (`discardPickBeforeShow`). A card taken from the discard pile cannot be discarded
 *      on the same turn.
 *  - SHOW ("seeing the joker"): reveal 3 melds, each a pure sequence (3 consecutive same-suit cards, no wild
 *    substitution; A-2-3 and Q-K-A valid, K-A-2 not) or a tunnela (3 identical cards). The player then sees the tiplu.
 *  - Wild cards (after show): every card of the tiplu's rank, plus Poplu/Jhiplu (same suit ±1) and printed jokers.
 *  - FINISH: a player who has shown declares the remaining 12 hand cards as 4 melds (sequence/trial/tunnela,
 *    wilds allowed) and discards the 22nd card. The server validates the full declaration.
 *  - Maal points (only for players who have shown): Tiplu 3, Poplu 2, Jhiplu 2, Alter 5, Man 2,
 *    Marriage (Jhiplu+Tiplu+Poplu) 10 instead of 7, each shown tunnela 5.
 *  - Settlement per round (zero-sum, in points): net_i = n·M_i − ΣM; each loser additionally pays the winner
 *    `seenPenalty` (3) if they had shown or `unseenPenalty` (10) if not. Points × `pointValue` = chips,
 *    losses capped at each player's chips with winners scaled down proportionally.
 *  - If the stock runs out, the discard pile (minus its top card) is reshuffled into the stock.
 *  - Turn timeout: draw from stock, discard the least useful card.
 */
import { buildDeck, removeCards, sortHand, type CardId } from '../../core/cards.js';
import { checkBool, checkEnum, checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import { nextSeatWhere, seatsWhere } from '../../core/seats.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type SeatSetup,
} from '../../core/types.js';
import {
  cardUtility,
  candidateMelds,
  isMeld,
  isPureMeld,
  maalPoints,
  partitionIntoMelds,
  type JokerContext,
  type MaalValues,
} from './melds.js';

export interface MarriageConfig {
  maxPlayers: number;
  minPlayers: number;
  decks: number;
  printedJokersPerDeck: number;
  pointValue: number;
  seenPenalty: number;
  unseenPenalty: number;
  maal: MaalValues;
  discardPickBeforeShow: 'never' | 'ifCompletesPure' | 'always';
  sameRankWild: boolean;
  neighboursWild: boolean;
  handEndDelayMs: number;
  startDelayMs: number;
}

export type MarriagePhase = 'WAITING' | 'DRAW' | 'DISCARD' | 'HAND_END';

export interface MarriagePlayer {
  playerId: string;
  chips: number;
  inHand: boolean;
  hand: CardId[];
  shown: CardId[][];
  hasShown: boolean;
  /** Card taken from the discard pile this turn (cannot be discarded back). */
  pickedFromDiscard: CardId | null;
}

export interface MarriageRoundResult {
  winner: number | null;
  reason: 'FINISH' | 'STOCK_EXHAUSTED' | 'LAST_PLAYER';
  tiplu: CardId;
  maal: Record<number, number>;
  points: Record<number, number>;
  chips: Record<number, number>;
  hands: Record<number, CardId[]>;
  melds: Record<number, CardId[][]>;
}

export interface MarriageState {
  config: MarriageConfig;
  seats: (MarriagePlayer | null)[];
  phase: MarriagePhase;
  handNo: number;
  dealer: number;
  turn: number;
  stock: CardId[];
  discard: CardId[];
  tiplu: CardId | null;
  lastResult: MarriageRoundResult | null;
  reshuffles: number;
  lastAction: { seat: number; action: string; card?: CardId } | null;
}

export type MarriageAction =
  | { type: 'draw'; from: 'stock' | 'discard' }
  | { type: 'show'; groups: CardId[][] }
  | { type: 'discard'; card: CardId }
  | { type: 'finish'; discard: CardId; groups: CardId[][] };

export interface MarriageLegal {
  canDrawStock: boolean;
  canDrawDiscard: boolean;
  canShow: boolean;
  canDiscard: boolean;
  canFinish: boolean;
  /** Card that may not be discarded this turn. */
  lockedCard: CardId | null;
}

export interface MarriagePlayerView {
  seat: number;
  playerId: string;
  chips: number;
  inHand: boolean;
  handCount: number;
  hasShown: boolean;
  shown: CardId[][];
}

export interface MarriageView {
  gameType: 'MARRIAGE';
  config: MarriageConfig;
  seat: number | null;
  phase: MarriagePhase;
  handNo: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  players: (MarriagePlayerView | null)[];
  stockCount: number;
  discardTop: CardId | null;
  discardPile: CardId[];
  /** Visible only to players who have shown (and to everyone at round end). */
  tiplu: CardId | null;
  lastResult: MarriageRoundResult | null;
  lastAction: MarriageState['lastAction'];
}

const DEFAULTS: MarriageConfig = {
  maxPlayers: 5,
  minPlayers: 2,
  decks: 3,
  printedJokersPerDeck: 0,
  pointValue: 10,
  seenPenalty: 3,
  unseenPenalty: 10,
  maal: { tiplu: 3, poplu: 2, jhiplu: 2, alter: 5, man: 2, marriage: 10, tunnela: 5 },
  discardPickBeforeShow: 'ifCompletesPure',
  sameRankWild: true,
  neighboursWild: true,
  handEndDelayMs: 8000,
  startDelayMs: 2500,
};

const HAND_SIZE = 21;
/** After this many stock reshuffles without a winner the round is void (prevents endless rounds). */
const MAX_RESHUFFLES = 3;

export function jokerContext(s: Pick<MarriageState, 'tiplu' | 'config'>): JokerContext | null {
  if (!s.tiplu) return null;
  return {
    tiplu: s.tiplu,
    sameRankWild: s.config.sameRankWild,
    neighboursWild: s.config.neighboursWild,
    printedJokersWild: s.config.printedJokersPerDeck > 0,
  };
}

/** Zero-sum cap: no player loses more than their chips; winners are scaled down proportionally. */
export function capZeroSum(deltas: Record<number, number>, chips: Record<number, number>): Record<number, number> {
  const out: Record<number, number> = {};
  let collected = 0;
  let positive = 0;
  for (const [k, d] of Object.entries(deltas)) {
    const seat = Number(k);
    if (d < 0) {
      const loss = Math.min(-d, chips[seat] ?? 0);
      out[seat] = -loss;
      collected += loss;
    } else positive += d;
  }
  const winners = Object.entries(deltas).filter(([, d]) => d > 0).sort((a, b) => b[1] - a[1]);
  let distributed = 0;
  for (const [k, d] of winners) {
    const g = positive === 0 ? 0 : Math.floor((d * collected) / positive);
    out[Number(k)] = g;
    distributed += g;
  }
  if (winners.length) out[Number(winners[0]![0])]! += collected - distributed;
  for (const k of Object.keys(deltas)) out[Number(k)] ??= 0;
  return out;
}

function inHandSeats(s: MarriageState): number[] {
  return seatsWhere(s.seats, (p) => p.inHand);
}

function drawFromStock(s: MarriageState, events: EngineEvent[], rng: Rng): CardId | null {
  if (s.stock.length === 0 && s.discard.length > 1) {
    const top = s.discard.pop()!;
    s.stock = shuffle(s.discard, rng);
    s.discard = [top];
    s.reshuffles++;
    events.push({ type: 'stockReshuffled', stockCount: s.stock.length });
  }
  return s.stock.pop() ?? null;
}

function canPickDiscard(s: MarriageState, p: MarriagePlayer): boolean {
  const top = s.discard.at(-1);
  if (!top) return false;
  if (p.hasShown) return true;
  const rule = s.config.discardPickBeforeShow;
  if (rule === 'always') return true;
  if (rule === 'never') return false;
  const hand = [...p.hand, top];
  const idx = hand.length - 1;
  return candidateMelds(hand, null, true).some((m) => m.includes(idx));
}

function legalFor(s: MarriageState, seat: number): MarriageLegal {
  const none: MarriageLegal = { canDrawStock: false, canDrawDiscard: false, canShow: false, canDiscard: false, canFinish: false, lockedCard: null };
  const p = s.seats[seat];
  if (!p || !p.inHand || s.turn !== seat) return none;
  if (s.phase === 'DRAW') {
    return { ...none, canDrawStock: s.stock.length > 0 || (s.discard.length > 1 && s.reshuffles < MAX_RESHUFFLES), canDrawDiscard: canPickDiscard(s, p) };
  }
  if (s.phase === 'DISCARD') {
    return { ...none, canDiscard: true, canShow: !p.hasShown, canFinish: p.hasShown, lockedCard: p.pickedFromDiscard };
  }
  return none;
}

function settleRound(s: MarriageState, winner: number | null, reason: MarriageRoundResult['reason'], events: EngineEvent[]): void {
  const cfg = s.config;
  const seats = inHandSeats(s);
  const tiplu = s.tiplu!;
  const maal: Record<number, number> = {};
  for (const seat of seats) {
    const p = s.seats[seat]!;
    maal[seat] = p.hasShown ? maalPoints([...p.hand, ...p.shown.flat()], tiplu, cfg.maal, p.shown) : 0;
  }
  const points: Record<number, number> = {};
  if (winner !== null) {
    const n = seats.length;
    const total = seats.reduce((a, x) => a + maal[x]!, 0);
    for (const seat of seats) points[seat] = n * maal[seat]! - total;
    for (const seat of seats) {
      if (seat === winner) continue;
      const penalty = s.seats[seat]!.hasShown ? cfg.seenPenalty : cfg.unseenPenalty;
      points[seat]! -= penalty;
      points[winner]! += penalty;
    }
  } else for (const seat of seats) points[seat] = 0;
  const raw: Record<number, number> = {};
  const chipsBefore: Record<number, number> = {};
  for (const seat of seats) {
    raw[seat] = points[seat]! * cfg.pointValue;
    chipsBefore[seat] = s.seats[seat]!.chips;
  }
  const chips = capZeroSum(raw, chipsBefore);
  for (const seat of seats) s.seats[seat]!.chips += chips[seat]!;
  const hands: Record<number, CardId[]> = {};
  const melds: Record<number, CardId[][]> = {};
  for (const seat of seats) {
    hands[seat] = [...s.seats[seat]!.hand];
    melds[seat] = s.seats[seat]!.shown.map((g) => [...g]);
  }
  s.lastResult = { winner, reason, tiplu, maal, points, chips, hands, melds };
  s.phase = 'HAND_END';
  events.push({ type: 'handEnd', ...s.lastResult });
}

function validateGroups(cards: CardId[], groups: unknown, count: number): CardId[][] {
  assertEngine(Array.isArray(groups) && groups.length === count, 'INVALID_ACTION', `Exactly ${count} groups required`);
  const flat: CardId[] = [];
  for (const g of groups as unknown[]) {
    assertEngine(Array.isArray(g) && g.length === 3 && g.every((c) => typeof c === 'string'), 'INVALID_ACTION', 'Each group needs 3 cards');
    flat.push(...(g as CardId[]));
  }
  const pool = [...cards];
  assertEngine(removeCards(pool, flat), 'INVALID_CARD', 'Declared cards are not in your hand');
  return groups as CardId[][];
}

function startHand(s: MarriageState, rng: Rng, events: EngineEvent[]): void {
  const cfg = s.config;
  const deck = shuffle(buildDeck({ decks: cfg.decks, printedJokersPerDeck: cfg.printedJokersPerDeck }), rng);
  s.handNo++;
  s.lastResult = null;
  s.lastAction = null;
  s.reshuffles = 0;
  const players = seatsWhere(s.seats, (p) => p.chips > 0);
  for (const seat of seatsWhere(s.seats, () => true)) {
    const p = s.seats[seat]!;
    p.inHand = players.includes(seat);
    p.hand = p.inHand ? sortHand(deck.splice(0, HAND_SIZE)) : [];
    p.shown = [];
    p.hasShown = false;
    p.pickedFromDiscard = null;
  }
  // Tiplu: never a printed joker.
  let tIdx = deck.findIndex((c) => !c.startsWith('JK'));
  if (tIdx < 0) tIdx = 0;
  s.tiplu = deck.splice(tIdx, 1)[0]!;
  s.discard = [deck.pop()!];
  s.stock = deck;
  s.dealer = nextSeatWhere(s.seats, s.dealer, (p) => p.inHand)!;
  s.turn = nextSeatWhere(s.seats, s.dealer, (p) => p.inHand)!;
  s.phase = 'DRAW';
  events.push({ type: 'handStart', handNo: s.handNo, dealer: s.dealer, players, discardTop: s.discard[0], stockCount: s.stock.length });
}

function advance(s: MarriageState, from: number): void {
  s.turn = nextSeatWhere(s.seats, from, (p) => p.inHand)!;
  s.phase = 'DRAW';
}

export function leastUsefulCard(hand: CardId[], ctx: JokerContext | null, locked: CardId | null): CardId {
  const util = cardUtility(hand, ctx);
  let best = -1;
  hand.forEach((c, i) => {
    if (c === locked) return;
    if (best < 0 || util[i]! < util[best]!) best = i;
  });
  return hand[best < 0 ? 0 : best]!;
}

export const marriageEngine: GameEngine<MarriageConfig, MarriageState, MarriageAction, MarriageView, MarriageLegal> = {
  gameType: 'MARRIAGE',
  kind: 'session',

  defaultConfig: () => structuredClone(DEFAULTS),

  normalizeConfig(input) {
    const c = mergeKnown(structuredClone(DEFAULTS), input);
    checkInt('maxPlayers', c.maxPlayers, 2, 5);
    checkInt('minPlayers', c.minPlayers, 2, c.maxPlayers);
    checkInt('decks', c.decks, 3, 4);
    checkInt('printedJokersPerDeck', c.printedJokersPerDeck, 0, 2);
    checkInt('pointValue', c.pointValue, 1, 1e7);
    checkInt('seenPenalty', c.seenPenalty, 0, 100);
    checkInt('unseenPenalty', c.unseenPenalty, 0, 100);
    checkEnum('discardPickBeforeShow', c.discardPickBeforeShow, ['never', 'ifCompletesPure', 'always'] as const);
    checkBool('sameRankWild', c.sameRankWild);
    checkBool('neighboursWild', c.neighboursWild);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    checkInt('startDelayMs', c.startDelayMs, 0, 60000);
    const m = { ...DEFAULTS.maal, ...(c.maal ?? {}) };
    for (const k of Object.keys(DEFAULTS.maal) as (keyof MaalValues)[]) checkInt(`maal.${k}`, m[k], 0, 100);
    c.maal = m;
    return c;
  },

  seatLimits: (c) => ({ min: c.minPlayers, max: c.maxPlayers }),

  createState(config, seats, rng) {
    const s: MarriageState = {
      config,
      seats: Array<MarriagePlayer | null>(config.maxPlayers).fill(null),
      phase: 'WAITING',
      handNo: 0,
      dealer: rng.int(config.maxPlayers),
      turn: 0,
      stock: [],
      discard: [],
      tiplu: null,
      lastResult: null,
      reshuffles: 0,
      lastAction: null,
    };
    for (const setup of seats) marriageEngine.addSeat!(s, setup);
    return s;
  },

  addSeat(s, setup: SeatSetup) {
    assertEngine(setup.seat >= 0 && setup.seat < s.seats.length && !s.seats[setup.seat], 'SEAT_TAKEN', 'Seat unavailable');
    const chips = setup.chips ?? 0;
    assertEngine(Number.isInteger(chips) && chips > 0, 'INVALID_AMOUNT', 'Buy-in must be positive');
    s.seats[setup.seat] = { playerId: setup.playerId, chips, inHand: false, hand: [], shown: [], hasShown: false, pickedFromDiscard: null };
    return [{ type: 'playerJoined', seat: setup.seat, playerId: setup.playerId, chips }];
  },

  removeSeat(s, seat) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'Seat empty');
    const events: EngineEvent[] = [];
    const inProgress = s.phase === 'DRAW' || s.phase === 'DISCARD';
    if (inProgress && p.inHand) {
      // Leaving mid-round counts as an unseen loss for settlement fairness: settle now with the
      // leaver as a loser if only one player would remain, otherwise just drop out (cards to discard bottom).
      const remaining = inHandSeats(s).filter((x) => x !== seat);
      if (remaining.length === 1) {
        settleRound(s, remaining[0]!, 'LAST_PLAYER', events);
      } else {
        const wasTurn = s.turn === seat;
        p.inHand = false;
        s.discard.unshift(...p.hand, ...p.shown.flat());
        p.hand = [];
        p.shown = [];
        if (wasTurn) advance(s, seat);
      }
    }
    s.seats[seat] = null;
    events.push({ type: 'playerLeft', seat });
    return { chips: p.chips, events };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    const isGroups = (g: unknown): g is CardId[][] =>
      Array.isArray(g) && g.length <= 7 && g.every((x) => Array.isArray(x) && x.length === 3 && x.every((c) => typeof c === 'string' && c.length <= 6));
    switch (raw.type) {
      case 'draw':
        if (raw.from === 'stock' || raw.from === 'discard') return { type: 'draw', from: raw.from };
        break;
      case 'discard':
        if (typeof raw.card === 'string' && raw.card.length <= 6) return { type: 'discard', card: raw.card };
        break;
      case 'show':
        if (isGroups(raw.groups)) return { type: 'show', groups: raw.groups };
        break;
      case 'finish':
        if (typeof raw.discard === 'string' && isGroups(raw.groups)) return { type: 'finish', discard: raw.discard, groups: raw.groups };
        break;
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Marriage action');
  },

  applyAction(s, seat, action, rng) {
    assertEngine(s.phase === 'DRAW' || s.phase === 'DISCARD', 'INVALID_PHASE', 'No round in progress');
    assertEngine(s.turn === seat, 'NOT_YOUR_TURN', 'Not your turn');
    const p = s.seats[seat]!;
    const legal = legalFor(s, seat);
    const events: EngineEvent[] = [];
    switch (action.type) {
      case 'draw': {
        assertEngine(s.phase === 'DRAW', 'INVALID_PHASE', 'You already drew');
        if (action.from === 'stock') {
          assertEngine(legal.canDrawStock, 'ILLEGAL_MOVE', 'Stock is empty');
          const card = drawFromStock(s, events, rng)!;
          p.hand.push(card);
          p.pickedFromDiscard = null;
          events.push({ type: 'drew', seat, from: 'stock', stockCount: s.stock.length });
        } else {
          assertEngine(legal.canDrawDiscard, 'ILLEGAL_MOVE', 'You cannot take that card now');
          const card = s.discard.pop()!;
          p.hand.push(card);
          p.pickedFromDiscard = card;
          events.push({ type: 'drew', seat, from: 'discard', card });
        }
        s.lastAction = { seat, action: `draw_${action.from}` };
        s.phase = 'DISCARD';
        return events;
      }
      case 'show': {
        assertEngine(legal.canShow, 'ILLEGAL_MOVE', 'You cannot show now');
        const groups = validateGroups(p.hand, action.groups, 3);
        assertEngine(groups.every((g) => isPureMeld(g)), 'ILLEGAL_MOVE', 'Show needs 3 pure sequences or tunnelas');
        removeCards(p.hand, groups.flat());
        p.shown = groups.map((g) => [...g]);
        p.hasShown = true;
        if (p.pickedFromDiscard && !p.hand.includes(p.pickedFromDiscard)) p.pickedFromDiscard = null;
        s.lastAction = { seat, action: 'show' };
        events.push({ type: 'shown', seat, groups: p.shown });
        return events;
      }
      case 'discard': {
        assertEngine(legal.canDiscard, 'INVALID_PHASE', 'Draw first');
        assertEngine(p.hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
        assertEngine(action.card !== p.pickedFromDiscard || p.hand.filter((c) => c === action.card).length > 1, 'ILLEGAL_MOVE', 'You cannot discard the card you just picked up');
        removeCards(p.hand, [action.card]);
        s.discard.push(action.card);
        p.pickedFromDiscard = null;
        s.lastAction = { seat, action: 'discard', card: action.card };
        events.push({ type: 'discarded', seat, card: action.card });
        advance(s, seat);
        return events;
      }
      case 'finish': {
        assertEngine(legal.canFinish, 'ILLEGAL_MOVE', 'You must show before finishing');
        assertEngine(p.hand.includes(action.discard), 'INVALID_CARD', 'Discard card not in hand');
        const rest = [...p.hand];
        removeCards(rest, [action.discard]);
        const groups = validateGroups(rest, action.groups, rest.length / 3);
        assertEngine(rest.length === groups.length * 3, 'ILLEGAL_MOVE', 'Every card must be in a meld');
        const ctx = jokerContext(s);
        assertEngine(groups.every((g) => isMeld(g, ctx)), 'ILLEGAL_MOVE', 'Invalid meld in declaration');
        p.hand = rest;
        p.shown = [...p.shown, ...groups.map((g) => [...g])];
        p.hand = [];
        s.discard.push(action.discard);
        s.lastAction = { seat, action: 'finish', card: action.discard };
        events.push({ type: 'finished', seat, groups: p.shown, discard: action.discard });
        settleRound(s, seat, 'FINISH', events);
        return events;
      }
    }
  },

  getActors(s) {
    if (s.phase === 'DISCARD') return [s.turn];
    if (s.phase === 'DRAW') {
      const l = legalFor(s, s.turn);
      return l.canDrawStock || l.canDrawDiscard ? [s.turn] : [];
    }
    return [];
  },

  getLegalActions: (s, seat) => legalFor(s, seat),

  getTimeoutAction(s, seat) {
    const p = s.seats[seat]!;
    if (s.phase === 'DRAW') return { type: 'draw', from: legalFor(s, seat).canDrawStock ? 'stock' : 'discard' };
    return { type: 'discard', card: leastUsefulCard(p.hand, p.hasShown ? jokerContext(s) : null, p.pickedFromDiscard) };
  },

  getView(s, seat) {
    const me = seat === null ? null : s.seats[seat];
    const revealTiplu = s.phase === 'HAND_END' || Boolean(me?.hasShown);
    return {
      gameType: 'MARRIAGE',
      config: s.config,
      seat,
      phase: s.phase,
      handNo: s.handNo,
      dealer: s.dealer,
      turn: s.turn,
      hand: me ? [...me.hand] : [],
      players: s.seats.map((p, i) =>
        p ? { seat: i, playerId: p.playerId, chips: p.chips, inHand: p.inHand, handCount: p.hand.length, hasShown: p.hasShown, shown: p.shown.map((g) => [...g]) } : null,
      ),
      stockCount: s.stock.length,
      discardTop: s.discard.at(-1) ?? null,
      discardPile: s.discard.slice(-12),
      tiplu: revealTiplu ? s.tiplu : null,
      lastResult: s.lastResult ? structuredClone(s.lastResult) : null,
      lastAction: s.lastAction,
    };
  },

  getSystemStep(s) {
    const ready = seatsWhere(s.seats, (p) => p.chips > 0).length >= s.config.minPlayers;
    if (s.phase === 'HAND_END') return { kind: 'nextHand', delayMs: s.config.handEndDelayMs };
    if (s.phase === 'WAITING' && ready) return { kind: 'startHand', delayMs: s.config.startDelayMs };
    // Safety valve: both stock and discard exhausted → void round.
    if (s.phase === 'DRAW') {
      const l = legalFor(s, s.turn);
      if (!l.canDrawStock && !l.canDrawDiscard) return { kind: 'voidRound', delayMs: 0 };
    }
    return null;
  },

  runSystemStep(s, rng) {
    const events: EngineEvent[] = [];
    if (s.phase === 'DRAW') {
      settleRound(s, null, 'STOCK_EXHAUSTED', events);
      return events;
    }
    if (s.phase !== 'HAND_END' && s.phase !== 'WAITING') return events;
    s.phase = 'WAITING';
    for (const p of s.seats) if (p) p.inHand = false;
    if (seatsWhere(s.seats, (p) => p.chips > 0).length >= s.config.minPlayers) startHand(s, rng, events);
    else events.push({ type: 'waitingForPlayers' });
    return events;
  },

  isFinished: () => false,
  isHandInProgress: (s) => s.phase === 'DRAW' || s.phase === 'DISCARD',
  getChips: (s) => Object.fromEntries(seatsWhere(s.seats, () => true).map((i) => [i, s.seats[i]!.chips])),
  getStandings: (s) => standingsFromScores(seatsWhere(s.seats, () => true).map((seat) => ({ seat, score: s.seats[seat]!.chips }))),
  getRoundsCompleted: (s) => (s.phase === 'HAND_END' ? s.handNo : Math.max(0, s.handNo - 1)),
};

export { partitionIntoMelds };
